import type { ReactNode } from 'react';
import { Button } from '@/components/ui/button';
import {
  SquaresFourIcon,
  ListBulletsIcon,
  ListDashesIcon,
  EnvelopeIcon,
  FilePdfIcon,
  XIcon,
} from '@phosphor-icons/react';
import type { ViewMode } from './ar-report-shared';
import { T, useGT } from 'gt-react';

interface ArReportResultsHeaderProps {
  resultCountText: string;
  viewMode: ViewMode;
  onViewModeChange: (mode: ViewMode) => void;
  isDownloading: boolean;
  isLoading: boolean;
  onDownloadPdf: () => void;
  // Bulk selection
  someSelected: boolean;
  selectedCount: number;
  onSendStatements: () => void;
  onClearSelection: () => void;
  // Optional extra actions (e.g. "Save Report" button)
  actions?: ReactNode;
  // Last-sync status, shown right-justified with the other toolbar items
  syncStatus?: string;
}

export function ArReportResultsHeader({
  resultCountText,
  viewMode,
  onViewModeChange,
  isDownloading,
  isLoading,
  onDownloadPdf,
  someSelected,
  selectedCount,
  onSendStatements,
  onClearSelection,
  actions,
  syncStatus,
}: ArReportResultsHeaderProps) {
  const gt = useGT();
  return (
    <div className="flex items-center justify-between px-1">
      <div className="flex items-center gap-3">
        <span className="text-sm text-muted-foreground">{resultCountText}</span>
        {someSelected && viewMode === 'grouped' && (
          <SelectionBar
            selectedCount={selectedCount}
            onSendStatements={onSendStatements}
            onClearSelection={onClearSelection}
          />
        )}
      </div>
      <div className="flex items-center gap-2">
        {syncStatus && (
          <span className="text-xs text-muted-foreground">{syncStatus}</span>
        )}
        <Button
          variant="outline"
          size="sm"
          className="h-7 text-xs"
          disabled={isDownloading || isLoading}
          onClick={onDownloadPdf}
        >
          <FilePdfIcon className="h-3.5 w-3.5 mr-1" />
          {isDownloading
            ? gt('Generating...', {
                context: 'AR report PDF download in progress',
              })
            : gt('Download PDF', { context: 'AR report PDF download button' })}
        </Button>
        {actions}
        <ViewModeButtons
          viewMode={viewMode}
          onViewModeChange={onViewModeChange}
        />
      </div>
    </div>
  );
}

/** The grouped view's "N selected · Send Statements · ✕" bar. */
function SelectionBar({
  selectedCount,
  onSendStatements,
  onClearSelection,
}: Pick<
  ArReportResultsHeaderProps,
  'selectedCount' | 'onSendStatements' | 'onClearSelection'
>) {
  const gt = useGT();
  return (
    <div className="flex items-center gap-2 rounded-md border border-blue-200 bg-blue-50 px-3 py-1.5">
      <span className="text-sm font-medium text-blue-800">
        {gt('{count} selected', {
          context: 'AR report selection count label',
          count: String(selectedCount),
        })}
      </span>
      <div className="h-4 w-px bg-blue-200" />
      <Button
        variant="ghost"
        size="sm"
        className="h-7 px-2 text-blue-700 hover:bg-blue-100 hover:text-blue-800"
        onClick={onSendStatements}
      >
        <EnvelopeIcon className="mr-1 h-4 w-4" />
        <T context="AR report button to send statements to selected customers">
          Send Statements
        </T>
      </Button>
      <Button
        variant="ghost"
        size="sm"
        onClick={onClearSelection}
        className="h-7 w-7 p-0 text-gray-500 hover:bg-gray-100 hover:text-gray-700"
      >
        <XIcon className="h-4 w-4" />
      </Button>
    </div>
  );
}

/** Grouped / grouped-detail / flat view toggles. */
function ViewModeButtons({
  viewMode,
  onViewModeChange,
}: Pick<ArReportResultsHeaderProps, 'viewMode' | 'onViewModeChange'>) {
  const gt = useGT();
  return (
    <>
      <Button
        variant={viewMode === 'grouped' ? 'default' : 'ghost'}
        size="sm"
        className="h-7 w-7 p-0"
        onClick={() => onViewModeChange('grouped')}
        title={gt('Grouped by customer', {
          context: 'AR report view mode toggle tooltip',
        })}
      >
        <SquaresFourIcon className="h-4 w-4" />
        <span className="sr-only">
          <T context="ar">Grouped view</T>
        </span>
      </Button>
      <Button
        variant={viewMode === 'grouped_detail' ? 'default' : 'ghost'}
        size="sm"
        className="h-7 w-7 p-0"
        onClick={() => onViewModeChange('grouped_detail')}
        title={gt('Grouped with invoice detail', {
          context: 'AR report view mode toggle tooltip',
        })}
      >
        <ListDashesIcon className="h-4 w-4" />
        <span className="sr-only">
          <T context="ar">Grouped detail view</T>
        </span>
      </Button>
      <Button
        variant={viewMode === 'flat' ? 'default' : 'ghost'}
        size="sm"
        className="h-7 w-7 p-0"
        onClick={() => onViewModeChange('flat')}
        title={gt('Flat invoice list', {
          context: 'AR report view mode toggle tooltip',
        })}
      >
        <ListBulletsIcon className="h-4 w-4" />
        <span className="sr-only">
          <T context="ar">Flat list view</T>
        </span>
      </Button>
    </>
  );
}
